import { format, isValid, parse } from 'date-fns'
import {
  firstNonEmpty,
  type RevisionHistoryFinding,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionHistory'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'

const SUPPORTED_DATE_FORMATS = [
  { format: 'yyyy-MM-dd' },
  { format: 'dd-MMM-yy' },
  { format: 'dd-MMM-yyyy' },
  { format: 'd-MMM-yy' },
  { format: 'd-MMM-yyyy' },
  { format: 'dd-MM-yyyy' },
  { format: 'MM-dd-yyyy' },
  { format: 'dd-MM-yy' },
  { format: 'MM-dd-yy' },
  { format: 'MMM-dd-yyyy' },
]

const DATE_SEPARATOR_RUN = /[\s.,/-]+/g

const CANONICAL_DAY_FORMAT = 'yyyy-MM-dd'
const MIN_PLAUSIBLE_YEAR = 1900
const MAX_PLAUSIBLE_YEAR = 2100
/** Fixed pivot so two-digit years resolve the same way regardless of today. */
const TWO_DIGIT_YEAR_REFERENCE = new Date(2000, 0, 1)

const REVISION_PREFIX = /^REV(?:ISION|\.)?\s*/

/**
 * Canonical form of a revision code for equality checks: trimmed, uppercased,
 * without a `REV` / `REV.` / `REVISION` prefix, and with leading zeros removed
 * from all-digit codes. Returns '' for a blank code.
 */
export function normalizeRevisionCodeForMatch(
  code: string | null | undefined
): string {
  const stripped = (code?.trim().toUpperCase() ?? '')
    .replace(REVISION_PREFIX, '')
    .trim()
  if (/^\d+$/.test(stripped)) return stripped.replace(/^0+(?=\d)/, '')
  return stripped
}

/**
 * Every `yyyy-MM-dd` day that `raw` can be read as. Ambiguous numeric dates
 * (e.g. `03/04/21`) yield both day/month orders; blank or unreadable input
 * yields an empty set. Separator-insensitive: `.`, `/`, `-`, spaces and commas
 * are interchangeable (`28.NOV.17`, `28/NOV/17` and `28-NOV-17` read alike).
 */
export function revisionDateCandidates(
  raw: string | null | undefined
): Set<string> {
  const candidates = new Set<string>()
  const text = raw?.trim().replace(DATE_SEPARATOR_RUN, '-')
  if (!text) return candidates
  for (const { format: dateFormat } of SUPPORTED_DATE_FORMATS) {
    const parsed = parse(text, dateFormat, TWO_DIGIT_YEAR_REFERENCE)
    if (!isValid(parsed)) continue
    const year = parsed.getFullYear()
    if (year < MIN_PLAUSIBLE_YEAR || year > MAX_PLAUSIBLE_YEAR) continue
    candidates.add(format(parsed, CANONICAL_DAY_FORMAT))
  }
  return candidates
}

function findingCodes(finding: RevisionHistoryFinding): Set<string> {
  const codes = new Set<string>()
  for (const value of [
    finding.revision_key,
    finding.raw_label,
    ...(finding.evidence_json ?? []).map((row) => row.raw_text),
  ]) {
    const code = normalizeRevisionCodeForMatch(value)
    if (code) codes.add(code)
  }
  return codes
}

function findingDates(finding: RevisionHistoryFinding): string[] {
  return [
    finding.raw_date,
    ...(finding.evidence_json ?? []).map((row) => row.raw_date),
  ].flatMap((value) => firstNonEmpty(value) ?? [])
}

/**
 * First member (latest-first) whose revision code AND at least one readable
 * date agree with the finding; null otherwise. A missing or unreadable date on
 * either side never matches.
 */
export function matchFindingToMember(
  finding: RevisionHistoryFinding,
  members: readonly RevisionFamilyMemberView[]
): RevisionFamilyMemberView | null {
  const codes = findingCodes(finding)
  const days = new Set(
    findingDates(finding).flatMap((d) => [...revisionDateCandidates(d)])
  )
  if (codes.size === 0 || days.size === 0) return null
  return (
    members.find(
      (member) =>
        codes.has(normalizeRevisionCodeForMatch(member.revisionCode)) &&
        [...revisionDateCandidates(member.revisionDate)].some((day) =>
          days.has(day)
        )
    ) ?? null
  )
}
